import React, { useEffect, useRef, useState } from 'react';
import maplibre, { LngLatBounds, Map as MlMap } from 'maplibre-gl';
import { centroid } from '@turf/turf';
import {
  getMapboxAccessToken,
  getMapStyle,
  transformRequest,
} from '@/utils/map';
import { AlmanacLocation, Frame } from '@/types/location';
import {
  BeeIconBlue,
  BeeIconColorful,
} from '@/components/ui/icons/map/almanac';

const SELECTED_SEGMENT_SOURCE_ID = 'hm-almanac-selected-segment-data';
const SELECTED_SEGMENT_LAYER_ID = 'hm-almanac-selected-segment-layer';
const SELECTED_SEGMENT_OUTLINE_LAYER_ID =
  'hm-almanac-selected-segment-outline-layer';

export type Props = {
  location: AlmanacLocation;
  /** changes the styles of the segment and icon. */
  variant: 'almanac' | 'scout';
  activeSequence?: Frame[];
  activeFrameIndex?: number;
};
export const MiniMap = ({
  location,
  variant,
  activeSequence,
  activeFrameIndex,
}: Props) => {
  const [map, setMap] = useState<MlMap | null>(null);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    (maplibre as any).accessToken = getMapboxAccessToken();
    if (!initialized.current && mapContainer.current) {
      const bounds = location.segments.coordinates
        .flatMap(segment => segment)
        .reduce(
          (bounds, [lng, lat]) => bounds.extend({ lng, lat }),
          new LngLatBounds(),
        );

      const [lng, lat] = centroid(location.geojson).geometry.coordinates;
      const newMap = new maplibre.Map({
        container: mapContainer.current,
        style: getMapStyle('light'),
        center: { lng, lat },
        transformRequest,
        maxPitch: 0,
        attributionControl: false,
        interactive: false,
        bounds,
        fitBoundsOptions: {
          padding: 60,
        },
      });

      newMap.on('load', () => {
        initialized.current = true;
        setMap(newMap);
      });

      newMap.on('error', e => {
        if (e && !e.error.message.includes('AJAXError')) {
          console.warn('Map error: ', e);
        }
      });
    }
  }, [location.geojson, location.segments.coordinates, mapContainer]);

  useEffect(() => {
    if (!map) return;

    map.addSource(SELECTED_SEGMENT_SOURCE_ID, {
      type: 'geojson',
      promoteId: 'id',
      data: {
        type: 'Feature',
        properties: {
          id: location._id,
          name: location.name,
        },
        geometry: location.segments,
      },
    });

    map.addLayer({
      id: SELECTED_SEGMENT_OUTLINE_LAYER_ID,
      source: SELECTED_SEGMENT_SOURCE_ID,
      type: 'line',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': 'black',
        'line-width': 10,
      },
    });
    map.addLayer({
      id: SELECTED_SEGMENT_LAYER_ID,
      source: SELECTED_SEGMENT_SOURCE_ID,
      type: 'line',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': 'white',
        'line-width': 5,
      },
    });

    return () => {
      if (map) {
        map.removeLayer(SELECTED_SEGMENT_OUTLINE_LAYER_ID);
        map.removeLayer(SELECTED_SEGMENT_LAYER_ID);
        map.removeSource(SELECTED_SEGMENT_SOURCE_ID);
      }
    };
  }, [location._id, location.name, location.segments, map, variant]);

  useEffect(() => {
    // Create a layer for the active frame
    if (
      !map ||
      !activeSequence ||
      !activeFrameIndex ||
      !activeSequence[activeFrameIndex]
    )
      return;

    const { position, bearing } = activeSequence[activeFrameIndex];

    const markerEl = document.createElement('div');
    markerEl.style.display = 'flex';
    markerEl.innerHTML =
      variant === 'almanac' ? BeeIconColorful() : BeeIconBlue;

    const marker = new maplibre.Marker({
      element: markerEl,
      draggable: false,
    });
    marker.setLngLat([position.lon, position.lat]).addTo(map);

    if (bearing) {
      marker.setRotation(bearing - map.getBearing());
    }

    return () => {
      if (marker) {
        marker.remove();
      }
    };
  }, [map, activeSequence, activeFrameIndex, variant]);

  return (
    <div
      ref={mapContainer}
      id="almanac-mini-map"
      className="absolute bottom-10 left-10 size-[300px] overflow-hidden rounded-[20px] border-[6px] border-neutral-1000 *:focus-visible:border-0"></div>
  );
};
